<template>
    <rkm-modal
        extra-large
        ref="RkmModal"
        title="Resultado de exame"
    >
        <rkm-form
            ref="RkmForm"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="12">
                    <rkm-form-input-autocomplete
                        :repository="repositories.exame"
                        :request-query-string-params="requestQueryStringParams"
                        :searchFields="fields"
                        :text-field="(item) => `${item.exigeCodigoAb ? item.codigo : formatProcedimento(item.codigo)} - ${item.nome}`"
                        :validations="validations.exame"
                        label="Exame"
                        v-model="model.exame"
                    />
                </rkm-col>
            </rkm-row>

            <template v-if="model.exame && getExamesEco().includes(model.exame.codigo)">
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-input
                            :validations="validations.idadeGestacionalSemanasEco"
                            label="Idade gestacional em semanas eco"
                            number
                            v-model="model.idadeGestacionalSemanasEco"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :validations="validations.idadeGestacionalDiasEco"
                            label="Idade gestacional em dias eco"
                            number
                            v-model="model.idadeGestacionalDiasEco"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-date-picker
                            label="DPP eco"
                            v-model="model.dppEco"
                        />
                    </rkm-col>
                </rkm-row>
            </template>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-date-picker
                        :max-date="data.additionals.dataAtual"
                        label="Data da solicitação"
                        v-model="model.solicitadoEm"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-date-picker
                        :max-date="data.additionals.dataAtual"
                        :min-date="others.moment(model.solicitadoEm).format('DD/MM/YYYY')"
                        :validations="validations.realizadoEm"
                        label="Data da realização"
                        v-model="model.realizadoEm"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-date-picker
                        :max-date="data.additionals.dataAtual"
                        :min-date="others.moment(model.realizadoEm).format('DD/MM/YYYY')"
                        label="Data do resultado"
                        v-model="model.resultadoEm"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="model.exame && getExamesPercentual().includes(model.exame.codigo)">
                <rkm-col cols="12">
                    <rkm-form-input
                        :validations="validations.percentual"
                        label="%"
                        type="number"
                        v-model="model.percentual"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="model.exame && getExamesPuericultura().includes(model.exame.codigo)">
                <rkm-col cols="12">
                    <rkm-form-radio-group
                        :options="resultadoTipos"
                        :validations="validations.resultadoTipo"
                        label="Tipo de resultado"
                        text-field="nome"
                        v-model="model.resultadoTipoId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :validations="validations.resultado"
                        label="Resultado"
                        rows="4"
                        v-model="model.resultado"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <template v-slot:footer="{cancel}">
            <rkm-button @click="cancel">
                Fechar
            </rkm-button>

            <rkm-button
                @click="save"
                color="primary"
            >
                {{item ? 'Salvar' : 'Incluir'}}
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Enums.
import procedimento from '@/enums/sigtap/procedimento'
import resultadoTipo from '@/enums/ambulatorio/resultadoTipo'

// Mixins.
import {ambulatorioAtendimentoExame, formats, object, repository} from '@/mixins'

// Others.
import moment from 'moment'

export default {
    computed: {
        requestQueryStringParams() {
            return {
                exige_procedimento: true,
                sexo_id: this.data.additionals.paciente.sexoId,
                atencao_basica_cadastro_exame_solicitado_avaliado: this.data.additionals.exibeBoxExameLaboratorialSolicitado ? true : false,
                atencao_basica_cadastro_triagem_neonatal: this.data.additionals.exibeBoxExameLaboratorialSolicitado ? true : false,
            }
        },
        resultadoTipos() {
            let resultadoTipos = []

            if (this.model.exame) {
                if (this.model.exame.codigo === this.enums.procedimento.FUNDOSCOPIA) {
                    resultadoTipos = this.data.form.resultadoExame.resultadoTipos.filter(resultadoTipo => {
                        return resultadoTipo.id === this.enums.resultadoTipo.NORMAL || resultadoTipo.id === this.enums.resultadoTipo.ALTERADO
                    })
                }
                else if (
                    this.model.exame.codigo === this.enums.procedimento.RESSONANCIA_MAGNETICA_DE_CRANIO
                    || this.model.exame.codigo === this.enums.procedimento.TOMOGRAFIA_COMPUTADORIZADA_DO_CRANIO
                    || this.model.exame.codigo === this.enums.procedimento.ULTRASSONOGRAFIA_TRANSFONTANELA
                ) {
                    resultadoTipos = this.data.form.resultadoExame.resultadoTipos.filter(resultadoTipo => {
                        return resultadoTipo.id === this.enums.resultadoTipo.NORMAL
                            || resultadoTipo.id === this.enums.resultadoTipo.SUGESTIVO_DE_INFECCAO_CONGENITA
                            || resultadoTipo.id === this.enums.resultadoTipo.OUTRAS_ALTERACOES
                            || resultadoTipo.id === this.enums.resultadoTipo.INDETERMINADO
                    })
                }
                else if (
                    this.model.exame.codigo === this.enums.procedimento.EMISSOES_OTOACUSTICAS_EVOCADAS_PARA_TRIAGEM_AUDITIVA_TESTE_DA_ORELHINHA
                    || this.model.exame.codigo === this.enums.procedimento.POTENCIAL_EVOCADO_AUDITIVO_PARA_TRIAGEM_AUDITIVA_TESTE_DA_ORELHINHA
                ) {
                    resultadoTipos = this.data.form.resultadoExame.resultadoTipos.filter(resultadoTipo => {
                        return resultadoTipo.id === this.enums.resultadoTipo.FALHOU
                            || resultadoTipo.id === this.enums.resultadoTipo.PASSOU
                    })
                }
            }

            return resultadoTipos
        },
        validations() {
            return {
                exame: {required: true},
                idadeGestacionalSemanasEco: {
                    max: 2,
                    min_value: 0,
                    max_value: 42,
                },
                idadeGestacionalDiasEco: {
                    max: 1,
                    min_value: 0,
                    max_value: 6,
                },
                realizadoEm: {required: true},
                percentual: {
                    required: true,
                    max: 4,
                    min_value: 0,
                    max_value: 100,
                },
                resultadoTipo: {required: true},
                resultado: {
                    required: true,
                    max: 5000,
                },
            }
        },
    },
    created() {
        this.repositories = {
            exame: this.getConectaSusRepository('parametrizacao.exame'),
        }
    },
    data: () => ({
        enums: {
            procedimento,
            resultadoTipo,
        },
        fields: [
            {key: 'codigo', searchable: true},
            {key: 'nome', searchable: true},
            {key: 'parametrizacao_procedimento.apelido', searchable: true},
        ],
        item: null,
        model: {
            exame: null,
            idadeGestacionalSemanasEco: null,
            idadeGestacionalDiasEco: null,
            dppEco: null,
            solicitadoEm: null,
            realizadoEm: null,
            resultadoEm: null,
            percentual: null,
            resultadoTipoId: null,
            resultado: null,
        },
        others: {moment},
    }),
    methods: {
        open(item = null) {
            this.item = item

            if (this.item) {
                this.edit()
            } else {
                this.create()
            }

            this.$refs.RkmModal.show()
        },
        create() {
            this.resetForm()
        },
        edit() {
            this.model = this.clone({
                exame: this.item.exame,
                idadeGestacionalSemanasEco: this.item.idadeGestacionalSemanasEco,
                idadeGestacionalDiasEco: this.item.idadeGestacionalDiasEco,
                dppEco: this.item.dppEco,
                solicitadoEm: this.item.solicitadoEm ? this.others.moment(this.item.solicitadoEm, 'DD/MM/YYYY').format('YYYY-MM-DD') : null,
                realizadoEm: this.others.moment(this.item.realizadoEm, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                resultadoEm: this.item.resultadoEm ? this.others.moment(this.item.resultadoEm, 'DD/MM/YYYY').format('YYYY-MM-DD') : null,
                percentual: this.item.percentual,
                resultadoTipoId: this.item.resultadoTipoId,
                resultado: this.item.resultado,
            })
        },
        store() {
            this.data.model.resultadosExames.push({
                exame: {
                    id: this.model.exame.id,
                    codigo: this.model.exame.codigo,
                    nome: this.model.exame.nome,
                    exigeCodigoAb: this.model.exame.exigeCodigoAb,
                },
                idadeGestacionalSemanasEco: this.model.idadeGestacionalSemanasEco,
                idadeGestacionalDiasEco: this.model.idadeGestacionalDiasEco,
                dppEco: this.model.dppEco,
                solicitadoEm: this.model.solicitadoEm ? this.others.moment(this.model.solicitadoEm).format('DD/MM/YYYY') : null,
                realizadoEm: this.others.moment(this.model.realizadoEm).format('DD/MM/YYYY'),
                resultadoEm: this.model.resultadoEm ? this.others.moment(this.model.resultadoEm).format('DD/MM/YYYY') : null,
                percentual: this.model.percentual,
                resultadoTipoId: this.model.resultadoTipoId,
                resultado: this.model.resultado,
            })
        },
        update() {
            Object.assign(this.item, {
                exame: {
                    id: this.model.exame.id,
                    codigo: this.model.exame.codigo,
                    nome: this.model.exame.nome,
                    exigeCodigoAb: this.model.exame.exigeCodigoAb,
                },
                idadeGestacionalSemanasEco: this.model.idadeGestacionalSemanasEco,
                idadeGestacionalDiasEco: this.model.idadeGestacionalDiasEco,
                dppEco: this.model.dppEco,
                solicitadoEm: this.model.solicitadoEm ? this.others.moment(this.model.solicitadoEm).format('DD/MM/YYYY') : null,
                realizadoEm: this.others.moment(this.model.realizadoEm).format('DD/MM/YYYY'),
                resultadoEm: this.model.resultadoEm ? this.others.moment(this.model.resultadoEm).format('DD/MM/YYYY') : null,
                percentual: this.model.percentual,
                resultadoTipoId: this.model.resultadoTipoId,
                resultado: this.model.resultado,
            })
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (this.item) {
                        this.update()
                    } else {
                        this.store()
                    }

                    this.$refs.RkmModal.hide()
                })
        },
        resetForm() {
            this.model.exame = null
            this.model.idadeGestacionalSemanasEco = null
            this.model.idadeGestacionalDiasEco = null
            this.model.dppEco = null
            this.model.solicitadoEm = null
            this.model.realizadoEm = null
            this.model.resultadoEm = null
            this.model.percentual = null
            this.model.resultadoTipoId = null
            this.model.resultado = null
        },
    },
    mixins: [ambulatorioAtendimentoExame, formats, object, repository],
    props: {
        data: {required: true, type: Object},
    },
}
</script>